/**
 * TanStack Query hooks for shipments. The query key shape:
 *   ['shipments', 'my', role]      — my shipments by role
 *   ['shipments', 'detail', id]    — single shipment (auto-refresh)
 *   ['shipments', 'track', tn]     — public tracking
 *
 * Invalidation: actions like cancel/approve-claim invalidate both the
 * detail key AND the relevant list key so the UI refreshes everywhere.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { shipmentsApi, type CreateShipmentInput } from '@/api/shipments';
import { isTerminal, type Shipment } from '@/types/shipment';

const POLL_INTERVAL_MS = 20_000;

export function useMyShipments(role: 'sender' | 'receiver' | 'driver' = 'sender') {
  return useQuery({
    queryKey: ['shipments', 'my', role],
    queryFn: () => shipmentsApi.listMy(role),
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });
}

export function useShipment(id: number | null) {
  return useQuery({
    queryKey: ['shipments', 'detail', id],
    queryFn: () => shipmentsApi.findById(id!),
    enabled: id != null,
    // Mirrors Flutter AutoRefreshMixin — poll every 20s while the shipment
    // is in a non-terminal state. TanStack stops polling automatically on
    // tab blur via refetchIntervalInBackground:false.
    refetchInterval: (q) => {
      const data = q.state.data as Shipment | undefined;
      if (!data) return POLL_INTERVAL_MS;
      return isTerminal(data.status) ? false : POLL_INTERVAL_MS;
    },
    refetchIntervalInBackground: false,
  });
}

export function useTrackShipment(trackingNumber: string | null) {
  return useQuery({
    queryKey: ['shipments', 'track', trackingNumber],
    queryFn: () => shipmentsApi.track(trackingNumber!),
    enabled: !!trackingNumber,
    refetchInterval: (q) => {
      const data = q.state.data as Shipment | undefined;
      return data && isTerminal(data.status) ? false : POLL_INTERVAL_MS;
    },
  });
}

export function useCreateShipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateShipmentInput) => shipmentsApi.create(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['shipments', 'my', 'sender'] });
    },
  });
}

export function useCancelShipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) =>
      shipmentsApi.cancel(id, reason),
    onSuccess: (_d, { id }) => {
      void qc.invalidateQueries({ queryKey: ['shipments', 'detail', id] });
      void qc.invalidateQueries({ queryKey: ['shipments', 'my'] });
    },
  });
}

export function useApproveClaim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => shipmentsApi.approveClaim(id),
    onSuccess: (_d, id) => {
      void qc.invalidateQueries({ queryKey: ['shipments', 'detail', id] });
      void qc.invalidateQueries({ queryKey: ['shipments', 'my'] });
    },
  });
}

export function useRejectClaim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) =>
      shipmentsApi.rejectClaim(id, reason),
    onSuccess: (_d, { id }) => {
      void qc.invalidateQueries({ queryKey: ['shipments', 'detail', id] });
      void qc.invalidateQueries({ queryKey: ['shipments', 'my'] });
    },
  });
}

// ── Driver-side hooks ────────────────────────────────────────────

export function usePendingShipments() {
  return useQuery({
    queryKey: ['shipments', 'pending'],
    queryFn: () => shipmentsApi.listPendingForDrivers(),
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });
}

export function useDriverShipments() {
  return useQuery({
    queryKey: ['shipments', 'my', 'driver'],
    queryFn: () => shipmentsApi.listMy('driver'),
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });
}

function invalidateAllShipmentQueries(qc: ReturnType<typeof useQueryClient>, id?: number) {
  void qc.invalidateQueries({ queryKey: ['shipments', 'my'] });
  void qc.invalidateQueries({ queryKey: ['shipments', 'pending'] });
  if (id != null) void qc.invalidateQueries({ queryKey: ['shipments', 'detail', id] });
}

export function useClaimShipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, tripId, offeredPrice }: { id: number; tripId?: number; offeredPrice?: number }) =>
      shipmentsApi.claim(id, { tripId, offeredPrice }),
    onSuccess: (_d, { id }) => invalidateAllShipmentQueries(qc, id),
  });
}

export function useAcceptShipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => shipmentsApi.accept(id),
    onSuccess: (_d, id) => invalidateAllShipmentQueries(qc, id),
  });
}

export function useRejectShipmentAsDriver() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) =>
      shipmentsApi.rejectByDriver(id, reason),
    onSuccess: (_d, { id }) => invalidateAllShipmentQueries(qc, id),
  });
}

export function useGeneratePickupOtp() {
  return useMutation({
    mutationFn: (id: number) => shipmentsApi.generatePickupOtp(id),
  });
}

export function useGenerateDeliveryOtp() {
  return useMutation({
    mutationFn: (id: number) => shipmentsApi.generateDeliveryOtp(id),
  });
}

export function useConfirmPickup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...opts }: Parameters<typeof shipmentsApi.confirmPickup>[1] & { id: number }) =>
      shipmentsApi.confirmPickup(id, opts),
    onSuccess: (_d, { id }) => invalidateAllShipmentQueries(qc, id),
  });
}

export function useConfirmDelivery() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...opts }: Parameters<typeof shipmentsApi.confirmDelivery>[1] & { id: number }) =>
      shipmentsApi.confirmDelivery(id, opts),
    onSuccess: (_d, { id }) => invalidateAllShipmentQueries(qc, id),
  });
}

export function useShortcodeLookup() {
  return useMutation({
    mutationFn: (shortCode: string) => shipmentsApi.lookup(shortCode),
  });
}
